import { useCallback, useRef } from 'react';
import { useUploadFiles, useDeleteFile } from 'api/queries/workflows/forms';

interface UseSurveyFileHandlersProps {
  associationId: string;
}

export const useSurveyFileHandlers = ({
  associationId
}: UseSurveyFileHandlersProps) => {
  const uploadFile = useUploadFiles();
  const deleteFile = useDeleteFile();

  // Track files that should be deleted after successful form submission
  const pendingDeletions = useRef<Set<string>>(new Set());

  const onUploadFiles = useCallback(
    (sender: any, options: any) => {
      const promises = options.files.map((file: File) =>
        uploadFile({ associationId, file })
      );
      Promise.all(promises)
        .then((results) => {
          const fileUrlsMap = new Map(
            results.map((result: any) => [result.originalName, result.url])
          );
          const response = options.files.map((file: any) => ({
            file,
            content: fileUrlsMap.get(file.name)
          }));

          options.callback(response);
        })
        .catch(() => {
          options.callback([], 'File upload failed');
        });
    },
    [associationId, uploadFile]
  );

  const onClearFiles = useCallback(async (sender: any, options: any) => {
    if (!options.value || options.value.length === 0) {
      return options.callback('success');
    }

    const filesToDelete = options.fileName
      ? options.value.filter((item) => item.name === options.fileName)
      : options.value;

    if (filesToDelete.length === 0) {
      return options.callback('error');
    }

    // Instead of deleting immediately, track them for deferred deletion
    filesToDelete.forEach((file) => {
      pendingDeletions.current.add(file.name);
    });

    // Update the UI immediately (optimistic update)
    const remainingFiles = options.value.filter(
      (file) =>
        !filesToDelete.some((deletedFile) => deletedFile.name === file.name)
    );

    if (options.fileName) {
      return options.callback('success', remainingFiles);
    }
    return options.callback('success', []);
  }, []);

  // Function to execute pending deletions (call this after successful form submission)
  const executePendingDeletions = useCallback(async () => {
    if (pendingDeletions.current.size === 0) {
      return;
    }

    const filesToDelete = Array.from(pendingDeletions.current);
    pendingDeletions.current.clear();

    try {
      await Promise.all(
        filesToDelete.map((filename) =>
          deleteFile({
            associationId,
            filename
          })
        )
      );
    } catch (error) {
      console.error('Failed to delete some files:', error);
      // Optionally, you could add files back to pendingDeletions for retry
    }
  }, [associationId, deleteFile]);

  // Function to clear pending deletions (call this if form submission is cancelled/failed)
  const clearPendingDeletions = useCallback(() => {
    pendingDeletions.current.clear();
  }, []);

  return {
    onUploadFiles,
    onClearFiles,
    executePendingDeletions,
    clearPendingDeletions
  };
};
